import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UserPlus,
  MoreHorizontal,
  Trash2,
  ArrowRightLeft,
  GripVertical,
  Download,
  FileSpreadsheet,
  FileText,
  Sparkles,
  RotateCcw,
  Pencil,
  Clock,
} from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import { Days, downloadAuthed } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UNITS } from "@/lib/api";
import DocumentationChips from "@/components/DocumentationChips";

function groupByProvider(day) {
  const map = {};
  (day.patients || []).forEach((p) => {
    const pid = p.provider_id || "__unassigned__";
    (map[pid] = map[pid] || []).push(p);
  });
  (day.unassigned || []).forEach((p) => {
    (map["__unassigned__"] = map["__unassigned__"] || []).push(p);
  });
  Object.values(map).forEach((arr) =>
    arr.sort((a, b) => (a.unit || "").localeCompare(b.unit || "") || a.name.localeCompare(b.name))
  );
  return map;
}

function PatientRow({ patient, providers, day, date, onChange, providerMap, isDragging: dragOverlay = false, complianceEvents }) {
  const [editOpen, setEditOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: patient.id,
    data: { patient },
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 }
    : undefined;

  const toggleStatus = async () => {
    const next = patient.status === "voluntary" ? "involuntary" : "voluntary";
    try {
      await Days.updatePatient(date, patient.id, { status: next });
      await onChange();
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const toggleNewAdmit = async () => {
    try {
      await Days.updatePatient(date, patient.id, { is_new: !patient.is_new });
      await onChange();
    } catch {
      toast.error("Failed to toggle new admit.");
    }
  };

  const toggleFollowup = async () => {
    try {
      await Days.updatePatient(date, patient.id, { is_followup: !patient.is_followup });
      await onChange();
    } catch {
      toast.error("Failed to toggle follow-up.");
    }
  };

  const reassign = async (newPid) => {
    try {
      await Days.updatePatient(date, patient.id, { provider_id: newPid });
      await onChange();
      toast.success("Reassigned.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed.");
    }
  };

  const remove = async () => {
    if (!confirm(`Remove ${patient.name}?`)) return;
    try {
      await Days.removePatient(date, patient.id);
      await onChange();
    } catch {
      toast.error("Delete failed.");
    }
  };

  // Room fallback: derive from note text like "Room 205-A" if the dedicated field is empty (older records).
  const roomDisplay =
    patient.room ||
    (patient.note && patient.note.match(/Room\s+([A-Za-z0-9\-]+)/i)?.[1]) ||
    "";
  const noteDisplay = (patient.note || "")
    .replace(/Room\s+[A-Za-z0-9\-]+/i, "")
    .replace(/^[\s·\-|,]+/, "")
    .trim();
  // LOS derivation: prefer the "(N)" trailing digit from the raw paste
  // (that's what the EMR export gives us) and fall back to computing from
  // the normalized admission_date so pre-existing patients still show a
  // flag when they've been on the unit a while.
  const losDays = (() => {
    const raw = (patient.admission_raw || "").trim();
    const m = raw.match(/\((\d+)\)/);
    if (m) return parseInt(m[1], 10);
    const d = (patient.admission_date || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const adm = new Date(d + "T00:00:00");
      if (!Number.isNaN(adm.getTime())) {
        return Math.max(0, Math.floor((Date.now() - adm.getTime()) / 86400000));
      }
    }
    return null;
  })();
  const losFlag =
    losDays === null
      ? null
      : losDays <= 3 && !patient.is_new
      ? { text: `⚠ LOS ${losDays}d`, kind: "short" }
      : losDays >= 14
      ? { text: `LOS ${losDays}d`, kind: "long" }
      : null;
  const csjDisplay = (patient.continued_stay_justification || "").trim();

  return (
    <div
      ref={dragOverlay ? undefined : setNodeRef}
      style={style}
      className={[
        "p-3 border-b border-[#e2e8f0] last:border-b-0 transition-colors group",
        isDragging
          ? "opacity-30"
          : patient.is_new
          ? "bg-[#fef7f3] hover:bg-[#fdf1ea]"
          : patient.is_followup
          ? "bg-[#f3ecfa] hover:bg-[#ede1f7]"
          : "hover:bg-[#f8fafc]",
        dragOverlay ? "bg-white shadow-2xl border border-[#0284c7] rounded-lg ring-2 ring-[#0284c7]/20" : "",
      ].join(" ")}
      data-testid={dragOverlay ? undefined : `patient-row-${patient.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        {/* Drag handle */}
        {!dragOverlay && (
          <button
            type="button"
            {...listeners}
            {...attributes}
            aria-label={`Drag ${patient.name} to reassign`}
            data-testid={`drag-handle-${patient.id}`}
            className="cursor-grab active:cursor-grabbing text-[#c4c4c0] hover:text-[#0284c7] transition-colors p-0.5 -ml-1 mt-0.5 touch-none no-print"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-medium text-[#0f172a] leading-tight" data-testid={`patient-name-${patient.id}`}>
              {patient.name}
            </div>
            {patient.is_new && (
              <span
                className="text-[10px] font-semibold tracking-wider uppercase text-[#d96c4e] border border-[#d96c4e] px-1.5 py-0.5 rounded"
                data-testid={`new-badge-${patient.id}`}
              >
                New
              </span>
            )}
            {patient.is_followup && (
              <span
                className="text-[10px] font-semibold tracking-wider uppercase text-[#6b4f8e] border border-[#6b4f8e] px-1.5 py-0.5 rounded"
                data-testid={`fu-badge-${patient.id}`}
              >
                F/U
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-[#545b58] font-mono flex-wrap">
            <span>{patient.admission_date || "—"}</span>
            {patient.unit && (
              <span className="px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#0f172a]">
                Unit {patient.unit}
              </span>
            )}
            {roomDisplay && (
              <span
                className="px-1.5 py-0.5 rounded bg-[#f0f9ff] text-[#0284c7]"
                data-testid={`patient-room-${patient.id}`}
              >
                Rm {roomDisplay}
              </span>
            )}
            {patient.projected_discharge && (
              <span
                className="px-1.5 py-0.5 rounded bg-[#f4efe4] text-[#8a6b1a] font-medium"
                data-testid={`patient-projected-dc-${patient.id}`}
                title="Projected discharge"
              >
                D/C {patient.projected_discharge}
              </span>
            )}
            {losFlag && (
              <span
                className={
                  losFlag.kind === "short"
                    ? "px-1.5 py-0.5 rounded bg-[#fbebe7] text-[#b94a48] font-semibold"
                    : "px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#545b58] font-medium"
                }
                data-testid={`patient-los-flag-${patient.id}`}
                title={
                  losFlag.kind === "short"
                    ? "Short stay — verify medical necessity before discharge"
                    : "Extended stay — check utilization review criteria"
                }
              >
                {losFlag.text}
              </span>
            )}
            <button
              type="button"
              onClick={toggleStatus}
              data-testid={`status-toggle-${patient.id}`}
              className={[
                "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium tracking-wide cursor-pointer transition-colors",
                patient.status === "voluntary"
                  ? "bg-[#e9f2ee] text-[#0284c7] hover:bg-[#d4e6dc]"
                  : "bg-[#fbebe7] text-[#d96c4e] hover:bg-[#f6d6ce]",
              ].join(" ")}
            >
              {patient.status === "voluntary" ? "Voluntary" : "Involuntary"}
            </button>
          </div>
          {noteDisplay && (
            <div className="mt-1 text-xs text-[#475569] italic truncate" title={noteDisplay}>
              {noteDisplay}
            </div>
          )}
          {csjDisplay && (
            <div
              className="mt-1 text-[11px] leading-snug text-[#0284c7] bg-[#f0f9ff] border-l-2 border-[#0284c7] pl-2 pr-1 py-0.5 rounded-sm"
              data-testid={`patient-csj-${patient.id}`}
              title="Continued stay justification"
            >
              <span className="font-semibold tracking-wider uppercase text-[10px] mr-1.5">CSJ:</span>
              {csjDisplay}
            </div>
          )}
          {!dragOverlay && complianceEvents && (
            <div className="mt-1.5 no-print">
              <DocumentationChips
                date={date}
                patientId={patient.id}
                events={complianceEvents}
                onSaved={onChange}
              />
            </div>
          )}
        </div>

        {!dragOverlay && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="opacity-60 group-hover:opacity-100 p-1 rounded hover:bg-[#f1f5f9] transition-opacity no-print"
              data-testid={`patient-menu-${patient.id}`}
            >
              <MoreHorizontal className="w-4 h-4 text-[#475569]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-white border-[#e2e8f0]">
            <DropdownMenuLabel className="text-[10px] tracking-widest uppercase text-[#475569]">
              Reassign to
            </DropdownMenuLabel>
            {providers.map((prov) => {
              const cnt = (day.patients || []).filter((p) => p.provider_id === prov.id).length;
              const full = cnt >= prov.max_patients;
              return (
                <DropdownMenuItem
                  key={prov.id}
                  disabled={prov.id === patient.provider_id || full}
                  onClick={() => reassign(prov.id)}
                  className="text-sm cursor-pointer"
                  data-testid={`reassign-to-${prov.id}-for-${patient.id}`}
                >
                  <ArrowRightLeft className="w-3.5 h-3.5 mr-2 text-[#0284c7]" />
                  {prov.name}{" "}
                  <span className="ml-auto text-xs text-[#475569] font-mono">
                    {cnt}/{prov.max_patients}
                  </span>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-sm cursor-pointer"
              onClick={() => setEditOpen(true)}
              data-testid={`edit-patient-${patient.id}`}
            >
              <Pencil className="w-3.5 h-3.5 mr-2 text-[#0284c7]" /> Edit patient
            </DropdownMenuItem>
            <DropdownMenuLabel className="text-[10px] tracking-widest uppercase text-[#475569]">
              Flag
            </DropdownMenuLabel>
            <DropdownMenuItem
              className="text-sm cursor-pointer"
              onClick={toggleNewAdmit}
              data-testid={`toggle-new-${patient.id}`}
            >
              <Sparkles className="w-3.5 h-3.5 mr-2 text-[#d96c4e]" />
              {patient.is_new ? "Unmark new admit" : "Mark as new admit"}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-sm cursor-pointer"
              onClick={toggleFollowup}
              data-testid={`toggle-fu-${patient.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5 mr-2 text-[#6b4f8e]" />
              {patient.is_followup ? "Unmark follow-up" : "Mark as follow-up"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-[#b94a48] cursor-pointer"
              onClick={remove}
              data-testid={`delete-patient-${patient.id}`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Remove patient
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>
      {!dragOverlay && (
        <EditPatientDialog
          patient={patient}
          providers={providers}
          date={date}
          open={editOpen}
          onOpenChange={setEditOpen}
          onChange={onChange}
        />
      )}
    </div>
  );
}

function AddPatientDialog({ providers, date, onChange, defaultProviderId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    admission_date: new Date().toISOString().slice(0, 10),
    unit: "",
    status: "voluntary",
    provider_id: defaultProviderId || "",
  });

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Patient name required.");
      return;
    }
    try {
      await Days.addPatient(date, form);
      await onChange();
      toast.success("Patient added.");
      setOpen(false);
      setForm({ ...form, name: "", unit: "", admission_date: new Date().toISOString().slice(0, 10) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="text-[#0284c7] hover:bg-[#e9f2ee] no-print"
          data-testid={`add-patient-to-${defaultProviderId || "unassigned"}`}
        >
          <UserPlus className="w-4 h-4 mr-1.5" /> Add
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Add patient</DialogTitle>
          <DialogDescription className="text-[#475569]">
            Manually add a patient to the current day&apos;s rounding list.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Name</Label>
            <Input
              className="mt-1 border-[#e2e8f0]"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="add-patient-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Admission</Label>
              <Input
                type="date"
                className="mt-1 border-[#e2e8f0]"
                value={form.admission_date}
                onChange={(e) => setForm({ ...form, admission_date: e.target.value })}
                data-testid="add-patient-date"
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="mt-1 border-[#e2e8f0]" data-testid="add-patient-unit">
                  <SelectValue placeholder="Choose unit" />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => (
                    <SelectItem key={u} value={u}>Unit {u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 border-[#e2e8f0]" data-testid="add-patient-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="voluntary">Voluntary</SelectItem>
                  <SelectItem value="involuntary">Involuntary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Provider</Label>
              <Select value={form.provider_id} onValueChange={(v) => setForm({ ...form, provider_id: v })}>
                <SelectTrigger className="mt-1 border-[#e2e8f0]" data-testid="add-patient-provider">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              onClick={submit}
              className="bg-[#0284c7] hover:bg-[#21473c] text-white"
              data-testid="submit-add-patient"
            >
              Add patient
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditPatientDialog({ patient, providers, date, open, onOpenChange, onChange }) {
  const [form, setForm] = useState(patient);
  const [saving, setSaving] = useState(false);

  // Reset form whenever the dialog is opened or the underlying patient changes.
  useEffect(() => {
    if (open) setForm(patient);
  }, [open, patient]);

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error("Patient name required.");
      return;
    }
    setSaving(true);
    try {
      // Only send fields the server accepts on PatientUpdate.
      await Days.updatePatient(date, patient.id, {
        name: form.name.trim(),
        admission_date: form.admission_date || "",
        unit: form.unit || "",
        room: (form.room || "").trim(),
        status: form.status || "voluntary",
        provider_id: form.provider_id || null,
        is_new: !!form.is_new,
        is_followup: !!form.is_followup,
        projected_discharge: (form.projected_discharge || "").trim(),
        continued_stay_justification: (form.continued_stay_justification || "").trim(),
        note: form.note || "",
      });
      await onChange();
      toast.success("Patient saved.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`edit-patient-dialog-${patient.id}`}>
        <DialogHeader>
          <DialogTitle className="font-heading">Edit patient</DialogTitle>
          <DialogDescription className="text-[#475569]">
            Update this patient&apos;s details. Changes save when you press Save.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Name</Label>
            <Input
              className="mt-1 border-[#e2e8f0]"
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid={`edit-patient-name-${patient.id}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Admission</Label>
              <Input
                type="date"
                className="mt-1 border-[#e2e8f0]"
                value={form.admission_date || ""}
                onChange={(e) => setForm({ ...form, admission_date: e.target.value })}
                data-testid={`edit-patient-date-${patient.id}`}
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Unit</Label>
              <Select value={form.unit || ""} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="mt-1 border-[#e2e8f0]" data-testid={`edit-patient-unit-${patient.id}`}>
                  <SelectValue placeholder="Choose unit" />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => (
                    <SelectItem key={u} value={u}>Unit {u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Room</Label>
              <Input
                className="mt-1 border-[#e2e8f0] font-mono"
                placeholder="305-A"
                value={form.room || ""}
                onChange={(e) => setForm({ ...form, room: e.target.value })}
                data-testid={`edit-patient-room-${patient.id}`}
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Status</Label>
              <Select value={form.status || "voluntary"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1 border-[#e2e8f0]" data-testid={`edit-patient-status-${patient.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="voluntary">Voluntary</SelectItem>
                  <SelectItem value="involuntary">Involuntary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Provider</Label>
            <Select
              value={form.provider_id || "__none__"}
              onValueChange={(v) => setForm({ ...form, provider_id: v === "__none__" ? null : v })}
            >
              <SelectTrigger className="mt-1 border-[#e2e8f0]" data-testid={`edit-patient-provider-${patient.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-[#0f172a] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.is_new}
                onChange={(e) => setForm({ ...form, is_new: e.target.checked })}
                data-testid={`edit-patient-is-new-${patient.id}`}
                className="w-4 h-4 accent-[#d96c4e]"
              />
              New admit
            </label>
            <label className="flex items-center gap-2 text-sm text-[#0f172a] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.is_followup}
                onChange={(e) => setForm({ ...form, is_followup: e.target.checked })}
                data-testid={`edit-patient-is-fu-${patient.id}`}
                className="w-4 h-4 accent-[#6b4f8e]"
              />
              F/U
            </label>
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Projected d/c</Label>
            <Input
              className="mt-1 border-[#e2e8f0] font-mono"
              placeholder="07/15 08:00"
              value={form.projected_discharge || ""}
              onChange={(e) => setForm({ ...form, projected_discharge: e.target.value })}
              data-testid={`edit-patient-projected-dc-${patient.id}`}
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">
              Continued stay justification
            </Label>
            <Input
              className="mt-1 border-[#e2e8f0]"
              placeholder="e.g. Ongoing SI; lithium subtherapeutic; awaiting family meeting"
              value={form.continued_stay_justification || ""}
              onChange={(e) =>
                setForm({ ...form, continued_stay_justification: e.target.value })
              }
              data-testid={`edit-patient-csj-${patient.id}`}
            />
            <p className="mt-1 text-[10.5px] text-[#475569] leading-tight">
              One line documenting today&apos;s medical necessity for continued inpatient stay.
              Prints on the rounding sheet and defends peer-to-peer / utilization review.
            </p>
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Notes</Label>
            <Input
              className="mt-1 border-[#e2e8f0]"
              value={form.note || ""}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid={`edit-patient-note-${patient.id}`}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-[#475569]"
              data-testid={`edit-patient-cancel-${patient.id}`}
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#0284c7] hover:bg-[#21473c] text-white"
              data-testid={`edit-patient-save-${patient.id}`}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProviderColumn({ cfg, prov, list, cap, day, date, onChange, providers, providerMap, availableProviders, isFull, complianceByPatientId }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `col:${cfg.provider_id}`,
    data: { providerId: cfg.provider_id, full: isFull },
  });
  const count = list.length;
  const capRatio = Math.min(1, count / cap);

  const savedTime = (day.provider_rounding_times || {})[cfg.provider_id] || "";
  const [roundingTime, setRoundingTime] = useState(savedTime);
  useEffect(() => setRoundingTime(savedTime), [savedTime]);

  const commitRoundingTime = async () => {
    if ((roundingTime || "").trim() === savedTime.trim()) return;
    try {
      await Days.setRoundingTime(date, cfg.provider_id, roundingTime);
      await onChange();
    } catch {
      toast.error("Failed to save rounding time.");
    }
  };
  return (
    <div
      className={[
        "flex flex-col bg-white border rounded-xl overflow-hidden h-full shadow-[0_2px_8px_rgba(0,0,0,0.02)] rounding-column print-break-avoid transition-colors",
        isOver && !isFull ? "border-[#0284c7] ring-2 ring-[#0284c7]/30" : isOver && isFull ? "border-[#b94a48] ring-2 ring-[#b94a48]/30" : "border-[#e2e8f0]",
      ].join(" ")}
      data-testid={`provider-column-${cfg.provider_id}`}
    >
      <div className="p-4 bg-[#f8fafc] border-b border-[#e2e8f0] provider-header">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-heading font-semibold text-lg text-[#0f172a] leading-tight" data-testid={`provider-name-${cfg.provider_id}`}>
              {prov.name}
            </h3>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-[#475569] font-mono" data-testid={`provider-count-${cfg.provider_id}`}>
                {count} / {cap} patients
              </span>
              {cfg.covers_php && (
                <span
                  className="bg-[#d96c4e] text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest"
                  data-testid={`php-badge-${cfg.provider_id}`}
                >
                  PHP
                </span>
              )}
              {prov.unit_preferences.length > 0 && (
                <span className="text-[10px] uppercase tracking-widest text-[#475569]">
                  {prov.unit_preferences.map((u) => `U${u}`).join(" · ")}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="text-[#475569] hover:text-[#0284c7] hover:bg-[#e9f2ee] p-1.5 rounded transition-colors no-print"
                  aria-label={`${prov.name} column actions`}
                  data-testid={`provider-actions-menu-${cfg.provider_id}`}
                  title="Column actions"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-[#e2e8f0]">
                <DropdownMenuLabel className="text-[10px] tracking-widest uppercase text-[#475569]">
                  Reassign {list.length} patient{list.length !== 1 ? "s" : ""}
                </DropdownMenuLabel>
                {providers
                  .filter((p) => p.id !== cfg.provider_id)
                  .map((p) => (
                    <DropdownMenuItem
                      key={p.id}
                      className="text-sm cursor-pointer"
                      disabled={list.length === 0}
                      onClick={async () => {
                        try {
                          const res = await Days.reassignAll(date, cfg.provider_id, p.id);
                          await onChange();
                          toast.success(`Moved ${res.moved} patient(s) → ${p.name}.`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed to reassign.");
                        }
                      }}
                      data-testid={`reassign-all-${cfg.provider_id}-to-${p.id}`}
                    >
                      <ArrowRightLeft className="w-3.5 h-3.5 mr-2 text-[#0284c7]" />
                      Move all → {p.name}
                    </DropdownMenuItem>
                  ))}
                {providers.filter((p) => p.id !== cfg.provider_id).length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-[#475569] italic">
                    Add another provider to move patients.
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="text-[#475569] hover:text-[#0284c7] hover:bg-[#e9f2ee] p-1.5 rounded transition-colors no-print"
                  aria-label={`Download ${prov.name}'s list`}
                  data-testid={`provider-download-menu-${cfg.provider_id}`}
                  title="Download this provider's list"
                >
                  <Download className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white border-[#e2e8f0]">
                <DropdownMenuLabel className="text-[10px] tracking-widest uppercase text-[#475569]">
                  Download {prov.name}
                </DropdownMenuLabel>
                <DropdownMenuItem
                  className="text-sm cursor-pointer"
                  onClick={() =>
                    downloadAuthed(
                      Days.exportProviderXlsx(date, cfg.provider_id),
                      `rounding_${date}_${prov.name.replace(/[^A-Za-z0-9]+/g, "_")}.xlsx`
                    ).catch(() => toast.error("Failed to download Excel."))
                  }
                  data-testid={`provider-download-xlsx-${cfg.provider_id}`}
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-2 text-[#0284c7]" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-sm cursor-pointer"
                  onClick={() =>
                    downloadAuthed(
                      Days.exportProviderDocx(date, cfg.provider_id),
                      `rounding_${date}_${prov.name.replace(/[^A-Za-z0-9]+/g, "_")}.docx`
                    ).catch(() => toast.error("Failed to download Word."))
                  }
                  data-testid={`provider-download-docx-${cfg.provider_id}`}
                >
                  <FileText className="w-3.5 h-3.5 mr-2 text-[#6b4f8e]" /> Word (.docx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AddPatientDialog
              providers={providers}
              date={date}
              onChange={onChange}
              defaultProviderId={cfg.provider_id}
            />
          </div>
        </div>
        <div className="mt-3 h-1 bg-[#f1f5f9] rounded-full overflow-hidden">
          <div
            className={[
              "h-full transition-all",
              capRatio >= 1 ? "bg-[#b94a48]" : capRatio >= 0.85 ? "bg-[#e5a93d]" : "bg-[#0284c7]",
            ].join(" ")}
            style={{ width: `${capRatio * 100}%` }}
          />
        </div>
        <div className="mt-3 flex items-center gap-2 no-print" data-testid={`rounding-time-row-${cfg.provider_id}`}>
          <Clock className="w-3.5 h-3.5 text-[#475569] flex-shrink-0" />
          <input
            type="text"
            value={roundingTime}
            onChange={(e) => setRoundingTime(e.target.value)}
            onBlur={commitRoundingTime}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            placeholder="Rounding time (e.g. 9:00 AM)"
            data-testid={`rounding-time-input-${cfg.provider_id}`}
            className="flex-1 bg-transparent border-b border-dashed border-[#c4c4c0] focus:border-[#0284c7] focus:outline-none text-xs text-[#0f172a] placeholder:text-[#c4c4c0] font-mono py-1"
          />
        </div>
        {savedTime && (
          <div
            className="mt-1 text-[11px] font-mono text-[#0284c7] hidden print:block"
            data-testid={`rounding-time-print-${cfg.provider_id}`}
          >
            Rounding: {savedTime}
          </div>
        )}
      </div>
      <div
        ref={setNodeRef}
        data-testid={`drop-zone-${cfg.provider_id}`}
        className={[
          "flex-1 min-h-[80px] transition-colors",
          isOver && !isFull ? "bg-[#f6f9f8]" : isOver && isFull ? "bg-[#fbebe7]" : "",
        ].join(" ")}
      >
        {list.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#909590] italic">
            {isOver ? (isFull ? "Column is full" : "Drop to assign") : "No patients assigned."}
          </div>
        ) : (
          list.map((p) => (
            <PatientRow
              key={p.id}
              patient={p}
              providers={availableProviders}
              day={day}
              date={date}
              onChange={onChange}
              providerMap={providerMap}
              complianceEvents={complianceByPatientId?.[p.id]}
            />
          ))
        )}
      </div>
    </div>
  );
}

function UnassignedColumn({ list, day, date, onChange, availableProviders, providerMap, complianceByPatientId }) {
  const { isOver, setNodeRef } = useDroppable({
    id: "col:__unassigned__",
    data: { providerId: null, full: false },
  });
  const empty = list.length === 0;

  // When there are 0 unassigned patients AND we're not being hovered, render a compact
  // always-mounted drop hint so useDroppable's node stays in the DOM. This lets isOver
  // flip to true and reveal the full column during a drag.
  if (empty && !isOver) {
    return (
      <div
        ref={setNodeRef}
        data-testid="drop-zone-unassigned"
        className="rounded-xl border-2 border-dashed border-[#e2e8f0] bg-transparent flex items-center justify-center p-4 text-[11px] uppercase tracking-widest text-[#c4c4c0] font-mono min-h-[80px] no-print"
      >
        Drop here to unassign
      </div>
    );
  }

  return (
    <div
      className={[
        "flex flex-col bg-white border-2 rounded-xl overflow-hidden h-full shadow-[0_2px_8px_rgba(0,0,0,0.02)] rounding-column print-break-avoid transition-colors",
        isOver ? "border-[#b94a48] ring-2 ring-[#b94a48]/30" : "border-[#b94a48]/40",
      ].join(" ")}
      data-testid="unassigned-column"
    >
      <div className="p-4 bg-[#fbebe7] border-b border-[#f6d6ce] provider-header">
        <h3 className="font-heading font-semibold text-lg text-[#b94a48]">Unassigned</h3>
        <div className="mt-1 text-xs text-[#b94a48] font-mono">
          {list.length} patient(s){list.length > 0 ? " — provider caps reached or manually unassigned" : ""}
        </div>
      </div>
      <div ref={setNodeRef} data-testid="drop-zone-unassigned" className="flex-1 min-h-[80px]">
        {empty ? (
          <div className="p-6 text-center text-sm text-[#b94a48] italic">Drop to unassign</div>
        ) : (
          list.map((p) => (
            <PatientRow
              key={p.id}
              patient={p}
              providers={availableProviders}
              day={day}
              date={date}
              onChange={onChange}
              providerMap={providerMap}
              complianceEvents={complianceByPatientId?.[p.id]}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function RoundingBoard({ day, providers, onChange, date, compliance }) {
  const providerMap = useMemo(() => Object.fromEntries(providers.map((p) => [p.id, p])), [providers]);
  const grouped = useMemo(() => groupByProvider(day), [day]);
  const [activePatient, setActivePatient] = useState(null);

  // Build a patient_id → events map from the compliance payload (if available).
  const complianceByPatientId = useMemo(() => {
    if (!compliance?.active) return {};
    const map = {};
    for (const row of compliance.active) {
      map[row.patient_id] = row.events;
    }
    return map;
  }, [compliance]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  // Render provider columns in alphabetical order by provider name
  // (independent of the order the user checked them on the roster).
  const orderedCfg = useMemo(() => {
    const cfg = day.available_providers || [];
    return [...cfg].sort((a, b) => {
      const na = providerMap[a.provider_id]?.name || "";
      const nb = providerMap[b.provider_id]?.name || "";
      return na.localeCompare(nb, undefined, { sensitivity: "base" });
    });
  }, [day.available_providers, providerMap]);
  const availableProviders = orderedCfg.map((c) => providerMap[c.provider_id]).filter(Boolean);

  const handleDragStart = (event) => {
    const p = event.active?.data?.current?.patient;
    if (p) setActivePatient(p);
  };

  const handleDragEnd = async (event) => {
    setActivePatient(null);
    const { active, over } = event;
    if (!over) return;
    const patient = active.data?.current?.patient;
    const targetProviderId = over.data?.current?.providerId; // null for unassigned
    if (!patient) return;

    // No-op if dropped in same column
    if (patient.provider_id === targetProviderId) return;
    if (patient.provider_id == null && targetProviderId == null) return;

    // Cap check
    if (targetProviderId) {
      const prov = providerMap[targetProviderId];
      if (!prov) return;
      const currentCount = (day.patients || []).filter((p) => p.provider_id === targetProviderId).length;
      if (currentCount >= prov.max_patients) {
        toast.error(`${prov.name} is at cap (${prov.max_patients}). Reassign someone else first.`);
        return;
      }
    }

    try {
      await Days.updatePatient(date, patient.id, { provider_id: targetProviderId });
      await onChange();
      const label = targetProviderId ? providerMap[targetProviderId]?.name : "Unassigned";
      toast.success(`${patient.name} → ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed.");
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActivePatient(null)}
    >
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-start"
        data-testid="rounding-board"
      >
        {orderedCfg.map((cfg) => {
          const prov = providerMap[cfg.provider_id];
          if (!prov) return null;
          const list = grouped[cfg.provider_id] || [];
          const isFull = list.length >= prov.max_patients;
          return (
            <ProviderColumn
              key={cfg.provider_id}
              cfg={cfg}
              prov={prov}
              list={list}
              cap={prov.max_patients}
              day={day}
              date={date}
              onChange={onChange}
              providers={providers}
              providerMap={providerMap}
              availableProviders={availableProviders}
              isFull={isFull}
              complianceByPatientId={complianceByPatientId}
            />
          );
        })}

        <UnassignedColumn
          list={grouped["__unassigned__"] || []}
          day={day}
          date={date}
          onChange={onChange}
          availableProviders={availableProviders}
          providerMap={providerMap}
          complianceByPatientId={complianceByPatientId}
        />
      </div>

      <DragOverlay dropAnimation={null}>
        {activePatient ? (
          <div className="w-[320px]">
            <PatientRow
              patient={activePatient}
              providers={availableProviders}
              day={day}
              date={date}
              onChange={onChange}
              providerMap={providerMap}
              isDragging={true}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
